import { AxiosInstance } from "axios";
import { SessionManager } from "../../auth/sessionManager";

// Centralized error format
export type NormalizedError = {
  status: number;
  message: string;
  code?: string | number;
  url?: string;
  method?: string;
  isNetwork?: boolean; // server unreachable / DNS / refused
  isTimeout?: boolean; // request timed out
  raw?: any;
  details?: any;
};

const isRefreshUrl = (url?: string) =>
  (url || "").includes("/api/v1/auth/refresh");
const isLoginUrl = (url?: string) => (url || "").includes("/api/login");
function isLogoutUrl(url: string) {
  return url.includes("/api/v1/auth/logout");
}
function isRegisterUrl(url: string) {
  return url.includes("/api/register");
}
export const setupCommonInterceptors = (instance: AxiosInstance) => {
  // Request interceptor -> attach session headers, CSRF, traceId
  instance.interceptors.request.use(async (config) => {
    config.headers = config.headers || {};
    config.withCredentials = true;

    // console.log({config});

    const url = config.url || "";
    const authFree =
      isRefreshUrl(url) ||
      isLoginUrl(url) ||
      isRegisterUrl(url) ||
      isLogoutUrl(url);

    if (!authFree) {
      if (!SessionManager.hasValidToken()) {
        await SessionManager.refreshToken();
      }
    }
    const accessKey = SessionManager.getAccessToken();
    if (accessKey) {
      (config.headers as any)["Authorization"] = `Bearer ${accessKey}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    async (response) => {
      return response.data;
    },
    async (err) => {
      const cfg = err?.config || {};
      const hasResponse = !!err?.response;

      // --- Network error ---
      if (!hasResponse) {
        const isTimeout = err?.code === "ECONNABORTED";
        const normalized: NormalizedError = {
          status: 0,
          message: isTimeout
            ? "Request timed out. Please try again."
            : "Internal Server Error.",
          code: err?.code || "ERR_NETWORK",
          url: (cfg.baseURL || "") + (cfg.url || ""),
          method: String(cfg.method || "GET").toUpperCase(),
          isNetwork: !isTimeout,
          isTimeout,
          raw: { message: err?.message },
        };
        return Promise.reject(normalized);
      }

      const status = err.response?.status ?? 500;
      const data = err.response?.data;

      const normalized: NormalizedError = {
        status, // HTTP status (e.g., 400)
        message:
          data?.message ||
          data?.error ||
          err?.message ||
          "Unknown error occurred",
        code: data?.statusCode ?? err?.code, // IMPORTANT: prefer API statusCode (e.g., 404)
        url: (cfg.baseURL || "") + (cfg.url || ""),
        method: String(cfg.method || "GET").toUpperCase(),
        raw: data ?? null,
        details: {
          httpStatus: status,
          apiStatusCode: data?.statusCode, // keep both
        },
      };

      // 401 refresh retry logic (same as you had, just safer)
      const isAccessExpired =
        status === 401 &&
        (data?.errors?.Auth?.includes?.("ACCESS_EXPIRED") ||
          String(data?.message || "")
            .toLowerCase()
            .includes("access token expired"));

      const alreadyRetried = (cfg as any).__retry === true;

      if (isAccessExpired && !alreadyRetried && !isRefreshUrl(cfg.url)) {
        (cfg as any).__retry = true;

        try {
          await SessionManager.refreshToken();
          const latest = SessionManager.getAccessToken();
          if (latest) {
            cfg.headers = cfg.headers || {};
            cfg.headers["Authorization"] = `Bearer ${latest}`;
          }
          return instance(cfg);
        } catch {
          return Promise.reject(normalized);
        }
      }

      return Promise.reject(normalized);
    },
  );

  // // Response interceptor -> normalize, persist raw payload, handle refresh
  // instance.interceptors.response.use(
  //   async (response) => {
  //     console.log({response});

  //     // Persist raw response (text/json/xml) centrally for recovery/audit
  //     // try {
  //     //   saveRawResponse({
  //     //     url: response.config.url || "",
  //     //     method: response.config.method || "GET",
  //     //     status: response.status,
  //     //     timestamp: Date.now(),
  //     //     raw: response.data,
  //     //   });
  //     // } catch (e) {
  //     //   console.warn("failed to persist raw response", e);
  //     // }
  //     return response.data;
  //   },
  //   async (err) => {
  //     console.log({err});

  //     const cfg = err.config || {};
  //     const status = err.response.status;
  //     const data = err.response?.data;
  //     const hasResponse = !!err.response;

  //     // --- Network error (server down / connection refused / DNS) ---
  //     if (!hasResponse) {
  //       const isTimeout = err.code === "ECONNABORTED";
  //       const normalized: NormalizedError = {
  //         status: 0,
  //         message: isTimeout
  //           ? "Request timed out. Please try again."
  //           : "Internal Server Error.", //"Please check your internet connection.",
  //         code: err.code || "internal server error.", //ERR_NETWORK
  //         url: (cfg.baseURL || "") + (cfg.url || ""),
  //         method: (cfg.method || "GET").toUpperCase(),
  //         isNetwork: !isTimeout,
  //         isTimeout,
  //         raw: { message: err.message },
  //       };
  //       return Promise.reject(normalized);
  //     }

  //     const normalized: NormalizedError = {
  //       status: status || 500,
  //       message:
  //         data?.message ||
  //         data?.error ||
  //         err.message ||
  //         "Unknown error occurred",
  //       code: err.code,
  //       url: (cfg.baseURL || "") + (cfg.url || ""),
  //       method: (cfg.method || "GET").toUpperCase(),
  //       raw: data ?? null,
  //     };

  //     // 401 handling: if access expired, refresh then retry once
  //     const isAccessExpired =
  //       status === 401 &&
  //       (data?.errors?.Auth?.includes?.("ACCESS_EXPIRED") ||
  //         String(data?.message || "")
  //           .toLowerCase()
  //           .includes("access token expired"));

  //     const alreadyRetried = (cfg as any).__retry === true;

  //     if (isAccessExpired && !alreadyRetried && !isRefreshUrl(cfg.url)) {
  //       (cfg as any).__retry = true;

  //       try {
  //         await SessionManager.refreshToken();
  //         const latest = SessionManager.getAccessToken();
  //         if (latest) {
  //           cfg.headers = cfg.headers || {};
  //           cfg.headers["Authorization"] = `Bearer ${latest}`;
  //         }
  //         return instance(cfg); // retry original request
  //       } catch (refreshErr) {
  //         return Promise.reject(normalized);
  //       }
  //     }

  //     return Promise.reject(normalized);
  //   }
  // );
};

// import { AxiosInstance } from "axios";
// import { SessionManager } from "../../auth/sessionManager";
// import { saveRawResponse } from "../../storages/rawResponseStore";
// import { isTokenExpired } from "services/auth/jwtUtils";

// // Centralized error format
// export type NormalizedError = {
//   status: number;
//   message: string;
//   code?: string | number;
//   url?: string;
//   method?: string;
//   isNetwork?: boolean; // server unreachable / DNS / refused
//   isTimeout?: boolean; // request timed out
//   raw?: any;
//   details?: any;
// };

// const isRefreshUrl = (url?: string) =>
//   (url || "").includes("/api/v1/auth/refresh");
// const isLoginUrl = (url?: string) => (url || "").includes("/api/login");
// function isLogoutUrl(url: string) {
//   return url.includes("/api/v1/auth/logout");
// }
// function isRegisterUrl(url: string) {
//   return url.includes("/api/register");
// }
// export const setupCommonInterceptors = (instance: AxiosInstance) => {
//   // Request interceptor -> attach session headers, CSRF, traceId
//   instance.interceptors.request.use(async (config) => {
//     config.headers = config.headers || {};
//     config.withCredentials = true;

//     // console.log({config});

//     const url = config.url || "";
//     const authFree =
//       isRefreshUrl(url) ||
//       isLoginUrl(url) ||
//       isRegisterUrl(url) ||
//       isLogoutUrl(url);

//     if (!authFree) {
//       if (!SessionManager.hasValidToken()) {
//         await SessionManager.refreshToken();
//       }
//     }
//     // if (!isRefreshUrl(url) && !isLoginUrl(url)) {
//     //   console.log("-----{ url }", { url });
//     //   console.log("----!isRefreshUrl(url)", !isRefreshUrl(url));
//     //   console.log("---!isLoginUrl(url)", !isLoginUrl(url));
//     //   if (!SessionManager.hasValidToken()) {
//     //     await SessionManager.refreshToken();
//     //   }
//     // }

//     const accessKey = SessionManager.getAccessToken();
//     if (accessKey) {
//       (config.headers as any)["Authorization"] = `Bearer ${accessKey}`;
//     }
//     return config;
//   });

//   // Response interceptor -> normalize, persist raw payload, handle refresh
//   instance.interceptors.response.use(
//     async (response) => {
//       // console.log({response});

//       // Persist raw response (text/json/xml) centrally for recovery/audit
//       // try {
//       //   saveRawResponse({
//       //     url: response.config.url || "",
//       //     method: response.config.method || "GET",
//       //     status: response.status,
//       //     timestamp: Date.now(),
//       //     raw: response.data,
//       //   });
//       // } catch (e) {
//       //   console.warn("failed to persist raw response", e);
//       // }

//       console.log({response});
//       console.log(response.data);
//       return response.data;
//     },
//     async (err) => {
//       // console.log({err});

//       const cfg = err.config || {};
//       const status = err.response.status;
//       const data = err.response?.data;
//       const hasResponse = !!err.response;

//       // --- Network error (server down / connection refused / DNS) ---
//       if (!hasResponse) {
//         const isTimeout = err.code === "ECONNABORTED";
//         const normalized: NormalizedError = {
//           status: 0,
//           message: isTimeout
//             ? "Request timed out. Please try again."
//             : "Internal Server Error.", //"Please check your internet connection.",
//           code: err.code || "internal server error.", //ERR_NETWORK
//           url: (cfg.baseURL || "") + (cfg.url || ""),
//           method: (cfg.method || "GET").toUpperCase(),
//           isNetwork: !isTimeout,
//           isTimeout,
//           raw: { message: err.message },
//         };
//         return Promise.reject(normalized);
//       }

//       const normalized: NormalizedError = {
//         status: status || 500,
//         message:
//           data?.message ||
//           data?.error ||
//           err.message ||
//           "Unknown error occurred",
//         code: err.code,
//         url: (cfg.baseURL || "") + (cfg.url || ""),
//         method: (cfg.method || "GET").toUpperCase(),
//         raw: data ?? null,
//       };

//       // 401 handling: if access expired, refresh then retry once
//       const isAccessExpired =
//         status === 401 &&
//         (data?.errors?.Auth?.includes?.("ACCESS_EXPIRED") ||
//           String(data?.message || "")
//             .toLowerCase()
//             .includes("access token expired"));

//       const alreadyRetried = (cfg as any).__retry === true;

//       if (isAccessExpired && !alreadyRetried && !isRefreshUrl(cfg.url)) {
//         (cfg as any).__retry = true;

//         try {
//           await SessionManager.refreshToken();
//           const latest = SessionManager.getAccessToken();
//           if (latest) {
//             cfg.headers = cfg.headers || {};
//             cfg.headers["Authorization"] = `Bearer ${latest}`;
//           }
//           return instance(cfg); // retry original request
//         } catch (refreshErr) {
//           return Promise.reject(normalized);
//         }
//       }

//       return Promise.reject(normalized);
//     }
//   );
// };

// // const token =
// //   SessionManager.getAccessToken() || Cookies.get("bbps_token") || null;

// // console.log("token",token);

// // if (token) {
// //   config.headers = config.headers ?? {};
// //   config.headers["Authorization"] = `Bearer ${token}`;
// //   SessionManager.setAccessToken(token);
// // }

// // 2) Attach API key or user-specific header if available
// // const apiKey = SessionManager.getUserApiKey();
// // if (apiKey) {
// //   (config.headers as any)["x-user-apikey"] = apiKey;

// //   // 3) Attach CSRF token if present
// //   const csrf = SessionManager.getCsrfToken();
// //   if (csrf) (config.headers as any)["x-csrf-token"] = csrf;

// //   // 4) Add a correlation id for tracing
// //   (config.headers as any)["x-correlation-id"] =
// //     SessionManager.createCorrelationId();
// // }

// // // --- HTTP error with response ---
// // const normalized: NormalizedError = {
// //   status: err.response!.status || 500,
// //   message:
// //     (err.response!.data &&
// //       (err.response!.data.message || err.response!.data.error)) ||
// //     err.message ||
// //     "Unknown error occurred",
// //   code: err.code,
// //   url: (cfg.baseURL || "") + (cfg.url || ""),
// //   method: (cfg.method || "GET").toUpperCase(),
// //   raw: err.response?.data ?? null,
// // };

// // If 401 -> attempt refresh once per instance using SessionManager queue
// // if (error.response?.status === 401 && !originalRequest?._retry) {
// //   originalRequest._retry = true;
// //   try {
// //     await SessionManager.refreshToken(); // internally queues multiple refresh requests
// //     return instance(originalRequest);
// //   } catch (refreshErr) {
// //     // refresh failed -> force logout (handled by SessionManager)
// //     SessionManager.clearAndRedirectToLogin();
// //     return Promise.reject({ status: 401, message: "Unauthorized" });
// //   }
// // }
